import { Color, Radius, Spacing } from 'zens-storybook'
import React, { Suspense } from 'react'
import { useTranslation } from 'react-i18next'
import { StyleSheet, View } from 'react-native'
import {
  useStore,
  Draft,
  LocationData,
  useCompanyMutation,
  useLocationMutation,
  DataWithUpload,
  CompanyData,
} from 'zens-store'
import { CompanyAddForm, CompanyAddFormData, PageHeader } from '@dms/component'
import { useRouter } from 'zens-router'

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    margin: Spacing.L,
    padding: Spacing.L,
    backgroundColor: Color.simplyWhite,
    borderRadius: Radius.M,
  },
})

const CompanyAdd = () => {
  const store = useStore()
  const { message } = store
  const router = useRouter()
  const mutationCompany = useCompanyMutation()
  const mutationLocation = useLocationMutation({ companyId: 0 })
  const { t } = useTranslation()

  const save = async (draft: Draft<DataWithUpload<CompanyAddFormData>>) => {
    try {
      const draftCompany = new Draft<CompanyData>({
        id: NaN,
        name: draft.data.name,
      })
      const company = await mutationCompany.create(draftCompany)
      if (!company) throw new Error('No company found')
      message.add({
        message: t('Company was created successfully'),
        status: 'success',
      })

      if (draft.data.location_enabled) {
        try {
          const draftLocation = new Draft<LocationData>({
            id: NaN,
            name: draft.data.location?.name,
            address: draft.data.address,
            company_id: company.id,
          })
          await mutationLocation.create(draftLocation)

          message.add({
            message: t('Location was created successfully'),
            status: 'success',
          })
        } catch (error) {
          const { message: errorMessage } = error as Error
          console.debug(`COMPANY: Location failed: ${errorMessage}`)
          message.add({
            message: `${t('Location could not be created')}. ${errorMessage}`,
            status: 'error',
          })
        }
      }
      draft.settle()
      router.push(`/companies/${company.id}`)
    } catch (error) {
      const { message: errorMessage } = error as Error
      console.debug(`COMPANY: Company failed: ${errorMessage}`)
      message.add({
        message: `${t('Company could not be created')}. ${errorMessage}`,
        status: 'error',
      })
    }
  }

  return (
    <View style={styles.container}>
      <Suspense fallback={<PageHeader.Skeleton breadcrumb title />}>
        <PageHeader title={t('Create new company')} />
      </Suspense>
      <View style={styles.content}>
        <CompanyAddForm onSave={save} />
      </View>
    </View>
  )
}

export default CompanyAdd
